import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  Modal,
  Divider,
  Tooltip,
  Slider as MuiSlider,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  CalendarToday,
  AccessTime,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Replay,
  Pause,
  Fullscreen,
  VolumeUp,
  VolumeOff,
  WarningAmber,
  Add,
  Delete,
} from '@mui/icons-material';
import { useAlertData } from '../../hooks/useAlertDataChanged';
import axiosInstance from '../../api/axiosInstance';
import { DateTime } from "luxon";

const PlaybackModal = ({ isOpen, onClose, cameraId, cameraTitle, assetId }) => {
  const videoRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('14:00');
  const [playbackData, setPlaybackData] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [hoverProgress, setHoverProgress] = useState(null);
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlertDescription, setNewAlertDescription] = useState('');
  const [newAlertType, setNewAlertType] = useState('warning');

  const { alerts, markAlertAsViewed, removeAlert: hookRemoveAlert, refreshAlerts: hookRefreshAlerts, setAlerts: hookSetAlerts } = useAlertData();
  const [localAlerts, setLocalAlerts] = useState(alerts || []);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toDeleteAlert, setToDeleteAlert] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const cameraAlerts = alerts.filter(alert => alert.cameraId === cameraId);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setLocalAlerts(alerts || []);
  }, [alerts]);

  useEffect(() => {
    if (!isOpen || !cameraId) return;
    setIsLoading(true);

    const fetchPlaybackFromAPI = async () => {
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        // Month start & end in UTC using Luxon
        const startDate = DateTime.utc(year, month + 1, 1, 0, 0, 0).toISO();
        const endDate = DateTime.utc(year, month + 1, DateTime.utc(year, month + 1).daysInMonth, 23, 59, 59).toISO();

        const apiData = await axiosInstance.get(
          `https://api.cognecto.com/api/streaming/playback/${assetId}?fromDate=${startDate}&toDate=${endDate}`
        );

        const groupedByDate = {};

        apiData?.data.forEach((item) => {
          const start = DateTime.fromISO(item.startTime, { zone: "UTC" });
          const end = DateTime.fromISO(item.endTime, { zone: "UTC" });

          const dateKey = start.toISODate();          // = YYYY-MM-DD
          const startTimeStr = start.toFormat("HH:mm");
          const endTimeStr = end.toFormat("HH:mm");

          if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];

          groupedByDate[dateKey].push({
            start: startTimeStr,
            end: endTimeStr,
            videoUrl: normalizeS3Url(item.s3Url)
          });
        });

        const finalOutput = Object.entries(groupedByDate).map(([date, ranges]) => ({
          date,
          timeRanges: ranges.map(r => ({
            start: r.start,
            end: r.end,
            videoUrl: r.videoUrl
          }))
        }));

        setPlaybackData(finalOutput);

        if (finalOutput.length > 0) {
          const lastDateISO = finalOutput[finalOutput.length - 1].date; // "2025-11-09"

          const luxonDate = DateTime.fromISO(lastDateISO, { zone: "UTC" });

          setSelectedDate(luxonDate.toJSDate());   // convert properly to JS Date
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error("Playback Fetch Error:", err);
      }
    };

    fetchPlaybackFromAPI();
  }, [isOpen, cameraId, currentMonth]);


  function normalizeS3Url(url) {
    return url.replace(
      /^s3:\/\/cog-streaming(?=\/processed_video)/,
      "https://cog-streaming.s3.ap-south-1.amazonaws.com"
    );
  }


  if (!isOpen) return null;

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateAvailable = (date) => {
    const dateStr = DateTime.fromJSDate(date).toISODate();
    return playbackData.some(data => data.date === dateStr);
  };

  const getDateAlerts = (date) => {
    const dateStr = DateTime.fromJSDate(date).toISODate();
    return cameraAlerts.filter(alert => alert.date === dateStr);
  };

  const getCurrentHourAlerts = () => {
    if (!selectedDate || !selectedTime) return [];
    const dateStr = DateTime.fromJSDate(selectedDate).toISODate();
    return cameraAlerts.filter(alert => alert.date === dateStr && alert.time === selectedTime);
  };

  const getAvailableTimeRanges = (date) => {
    const dateStr = DateTime.fromJSDate(date).toISODate();
    const data = playbackData.find(data => data.date === dateStr);
    return data?.timeRanges || [];
  };

  const isTimeInRange = (time, ranges) => {
    return ranges.some(range => time >= range.start && time <= range.end);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (seconds) => {

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimestampFromProgress = (progress) => {
    if (!selectedTime || !selectedDate) return '';

    const [hour] = selectedTime.split(':').map(Number);

    const totalSeconds = (progress / 100) * 3600;
    const progressMinutes = Math.floor(totalSeconds / 60);
    const progressSeconds = Math.floor(totalSeconds % 60);

    const exactHour = hour;
    const exactMinute = progressMinutes;
    const exactSecond = progressSeconds;

    const dateStr = selectedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const timeStr = `${exactHour.toString().padStart(2, '0')}:${exactMinute.toString().padStart(2, '0')}:${exactSecond.toString().padStart(2, '0')}`;

    return `${dateStr} ${timeStr}`;
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
    setSelectedVideo(null)
    setSelectedRangeIndex(null)
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      slots.push(timeStr);
    }
    return slots;
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newProgress = (clickX / rect.width) * videoDuration;
    videoRef.current.currentTime = Math.max(0, Math.min(videoDuration, newProgress));
  };

  const handleProgressMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const progress = (mouseX / rect.width) * 100;
    setHoverProgress(Math.max(0, Math.min(100, progress)));
  };

  const handleProgressMouseEnter = () => {
    setShowTimestamp(true);
  };

  const handleProgressMouseLeave = () => {
    setShowTimestamp(false);
    setHoverProgress(null);
  };

  const skipTime = (seconds) => {
    const progressChange = (seconds / 3600) * 100;
    setCurrentProgress(prev => Math.max(0, Math.min(100, prev + progressChange)));
  };

  const addAlert = () => {
    if (!selectedDate || !selectedTime || !newAlertDescription.trim()) return;

    setNewAlertDescription('');
    setShowAddAlert(false);
  };

  const deleteAlert = (alertId) => {
    console.log('Delete alert:', alertId);
  };

  const handleRequestDelete = (alertObj) => {
    setToDeleteAlert(alertObj);
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setToDeleteAlert(null);
    setConfirmDeleteOpen(false);
  };

  const handleCloseSnackbar = (_evt, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  const handleConfirmDelete = async () => {
    if (!toDeleteAlert || !toDeleteAlert.id) {
      setConfirmDeleteOpen(false);
      return;
    }

    const id = toDeleteAlert.id;
    setDeletingId(id);
    setConfirmDeleteOpen(false);
    const previousAlerts = localAlerts;

    setLocalAlerts(prev => prev.filter(a => a.id !== id));

    if (typeof hookRemoveAlert === 'function') {
      try {
        hookRemoveAlert(id);
      } catch (e) {
      }
    } else if (typeof hookSetAlerts === 'function') {
      try {
        hookSetAlerts(prev => prev.filter(a => a.id !== id));
      } catch (e) { }
    }

    try {
      const resp = await fetch(`/api/alerts/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!resp.ok) {
        setLocalAlerts(previousAlerts);
        if (typeof hookRefreshAlerts === 'function') {
          try { await hookRefreshAlerts(); } catch (e) { /* ignore */ }
        }
        throw new Error(`Delete failed: ${resp.status}`);
      }

      if (typeof hookRefreshAlerts === 'function') {
        try { await hookRefreshAlerts(); } catch (e) { /* ignore */ }
      }

      setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
      setToDeleteAlert(null);
    } catch (err) {
      console.error('delete error', err);
      setSnackbar({ open: true, message: 'Delete failed — try again', severity: 'error' });
    } finally {
      setDeletingId(null);
    }
  };



  const jumpToAlert = (alert) => {
    const alertDate = new Date(alert.date);
    setSelectedDate(alertDate);
    setSelectedTime(alert.time);
    setCurrentProgress(alert.progress);
    markAlertAsViewed(alert.id);
  };

  const getAlertTypeColor = (type) => {
    switch (type) {
      case 'critical':
        return 'error.main';
      case 'warning':
        return 'warning.main';
      case 'info':
        return 'info.main';
      default:
        return 'grey.500';
    }
  };

  const getAlertTypeBorder = (type) => {

    switch (type) {
      case 'critical':
        return { border: 1, borderColor: '#FECACA', bgcolor: '#FEE2E2' };
      case 'warning':
        return { border: 1, borderColor: '#FDE68A', bgcolor: '#FFFBEB' };
      case 'info':
        return { border: 1, borderColor: '#BFDBFE', bgcolor: '#DBEAFE' };
      default:
        return { border: 1, borderColor: '#E5E7EB', bgcolor: '#F9FAFB' };
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <Box key={`empty-${i}`} sx={{ height: 28 }} />
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isAvailable = isDateAvailable(date);
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
      const isToday = new Date().toDateString() === date.toDateString();
      const isFuture = date > new Date();
      const dateAlerts = getDateAlerts(date) || [];
      const hasAlerts = dateAlerts.length > 0;
      const hasUnviewedAlerts = dateAlerts.some(a => !a.viewed);

      cells.push(
        <Box key={`d-${day}`} sx={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Button
            onClick={() => !isFuture && setSelectedDate(date)}
            disabled={isFuture}
            disableRipple
            sx={{
              height: 28,
              width: 28,
              minWidth: 28,
              p: 0,
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 500,
              lineHeight: '28px',
              transition: 'all 0.2s',
              position: 'relative',
              textTransform: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
              color: isFuture ? '#D1D5DB' : '#0F172A',
              overflow: 'visible',
              ...(isSelected && {
                bgcolor: '#2563EB',
                color: '#FFFFFF',
                boxShadow: '0 8px 20px rgba(2,6,23,0.08)'
              }),
              ...(!isSelected && isAvailable && !isFuture && {
                bgcolor: '#ECFDF5',
                color: '#166534',
                '&:hover': { bgcolor: '#D1FAE5' },
                border: '1px solid #BBF7D0'
              }),
              ...(!isSelected && !isAvailable && !isFuture && {
                color: '#9CA3AF',
                '&:hover': { bgcolor: '#F8FAFC' }
              }),
              ...(isToday && !isSelected && {
                outline: '2px solid rgba(59,130,246,0.18)',
                outlineOffset: 0
              })
            }}
          >
            {day}

            {isAvailable && !isSelected && !hasAlerts && (
              <Box sx={{
                position: 'absolute',
                bottom: 2,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 4,
                height: 4,
                bgcolor: '#16A34A',
                borderRadius: '50%',
                zIndex: 2
              }} />
            )}

            {hasUnviewedAlerts && !isSelected && (
              <Box sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 8,
                height: 8,
                bgcolor: '#EF4444',
                borderRadius: '50%',
                animation: 'pulse 2s infinite',
                zIndex: 3
              }} />
            )}
          </Button>
        </Box>
      );
    }

    while (cells.length % 7 !== 0) {
      cells.push(<Box key={`trail-${cells.length}`} sx={{ height: 28 }} />);
    }

    return cells;
  };

  const handlePlayPause = async () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      if (video.paused)
        await video.play();
      else
        await video.pause();
    } catch (error) {

      if (error.name !== "AbortError")
        console.error("Error controlling video", error);
    }
  };

  const handlePlaybackRateChange = (e) => {
    const newRate = Number(e.target.value);
    setPlaybackSpeed(newRate);

    const video = videoRef.current;
    if (video) {
      video.playbackRate = newRate;
    }
  }

  const availableTimeRanges = getAvailableTimeRanges(selectedDate);
  const timeSlots = generateTimeSlots();
  const currentHourAlerts = getCurrentHourAlerts();

  const showVideoForTimeRange = (rangeObj, index) => {
    setSelectedVideo(rangeObj);
    setSelectedRangeIndex(index);
    setCurrentTime(null);
    setCurrentProgress(null);
    setVideoDuration(null);
  }

  const onModalClose = () => {
    setSelectedVideo(null);
    setSelectedRangeIndex(null);
    onClose();
  }

  const CalendarSkeletonWithLoader = ({ isLoading }) => (
    <Box
      sx={{
        position: 'relative',
        minHeight: 220,
      }}
    >
      {/* Skeleton grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {Array.from({ length: 42 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 28,
              borderRadius: 1,
              bgcolor: 'grey.100',
            }}
          />
        ))}
      </Box>

      {/* Centered loader overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // optional subtle overlay effect:
            bgcolor: 'rgba(255,255,255,0.4)',
            backdropFilter: 'blur(1px)',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}
    </Box>
  );

  return (
    <Modal open={isOpen}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: isFullscreen ? '100%' : '90vw',
          height: isFullscreen ? '100%' : '95vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography variant="h6" color="text.primary">
              Video Playback
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cameraTitle} ({cameraId})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              sx={{ '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Fullscreen sx={{ fontSize: 16, color: 'grey.500' }} />
            </IconButton>
            <IconButton
              onClick={() => onModalClose()}
              sx={{ '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Close sx={{ fontSize: 16, color: 'grey.500' }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
          <Box
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              flexShrink: 0,
              width: isFullscreen ? 320 : 288,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'auto'
            }}
          >

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                  <CalendarToday sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                  Date
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton onClick={() => navigateMonth('prev')} size="small" sx={{ p: 1, borderRadius: 1, '&:hover': { bgcolor: '#F3F4F6' } }}>
                    <ChevronLeftIcon sx={{ fontSize: 14, color: 'grey.600' }} />
                  </IconButton>

                  <Typography sx={{ minWidth: '90px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
                    {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </Typography>

                  <IconButton onClick={() => navigateMonth('next')} size="small" sx={{ p: 1, borderRadius: 1, '&:hover': { bgcolor: '#F3F4F6' } }}>
                    <ChevronRightIcon sx={{ fontSize: 14, color: 'grey.600' }} />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', mb: 1 }}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <Box key={`h-${i}`} sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>{d}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ minHeight: 180 }}>
                {!isLoading ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                    {renderCalendar()}
                  </Box>
                ) : (
                  <CalendarSkeletonWithLoader isLoading={isLoading} />
                )}
              </Box>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#ECFDF5', border: '1px solid', borderColor: '#BBF7D0', borderRadius: 0.5 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Available</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#F3F4F6', borderRadius: 0.5 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>No Data</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#EF4444', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Unviewed Alert</Typography>
                  </Box>
                </Box>
              </Box>

              <style>{`
                @keyframes pulse {
                  0% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.4); opacity: 0.6; }
                  100% { transform: scale(1); opacity: 1; }
                }
              `}</style>
            </Box>

            <Divider sx={{ borderColor: 'divider', flexShrink: 0 }} />

            {/* Selected Date */}
            <Box sx={{ pt: 2, flexShrink: 0 }}>
              <Typography component={'div'} variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Selected:
              </Typography>
              <Typography component={'div'} variant="caption" fontWeight={500} color="text.primary">
                {formatDate(selectedDate)}
              </Typography>

              {/* Date Alerts */}
              {getDateAlerts(selectedDate).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#4B5563',
                      fontSize: '0.75rem',
                      gap: '4px',
                    }}
                  >
                    <WarningAmber sx={{ fontSize: 12, color: '#D97706' }} />
                    Alerts for this date:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {getDateAlerts(selectedDate).map((alert) => (
                      <Box
                        key={alert.id}
                        onClick={() => jumpToAlert(alert)}
                        className="alert-item"
                        sx={{
                          position: 'relative',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          px: 2,
                          py: 1.25,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,

                          ...getAlertTypeBorder(alert.type),
                          ...(!alert.viewed && { border: 1, borderColor: '#FCA5A5' }),
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 18px rgba(15,23,42,0.06)',
                          },
                          '&:hover .deleteBtn': {
                            opacity: 1,
                            transform: 'scale(1)',
                          },
                          overflow: 'visible',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: getAlertTypeColor(alert.type),
                                ...(
                                  !alert.viewed
                                    ? {
                                      animation: 'pulse 2s infinite',
                                      '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.5 }
                                      }
                                    }
                                    : {}
                                ),
                              }}
                            />
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                              {alert.time}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', textTransform: 'capitalize' }}>
                              ({alert.type})
                            </Typography>
                            {!alert.viewed && (
                              <Typography sx={{
                                fontSize: '0.65rem',
                                color: '#DC2626',
                                fontWeight: 'bold'
                              }}>
                                NEW
                              </Typography>
                            )}
                          </Box>
                          {/* <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAlert(alert.id);
                            }}
                            sx={{
                              padding: '0.125rem',
                              borderRadius: 1,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              '&:hover': { 
                                backgroundColor: '#FEE2E2',
                                opacity: 1 
                              },
                              '&:parent:hover': { opacity: 1 }
                            }}
                          >
                            <Delete sx={{ fontSize: 16, color: '#EF4444' }} />
                          </Box> */}
                          {/*  Delete Icon — hidden by default, appears on alert hover */}
                          <IconButton
                            className="deleteBtn"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestDelete(alert);
                            }}
                            sx={{
                              opacity: 0,
                              transform: 'scale(0.9)',
                              transition: 'opacity 0.2s ease, transform 0.2s ease',
                              '&:hover': { bgcolor: '#FEE2E2' },
                              '@media (hover: none)': {
                                opacity: 1,
                                transform: 'scale(1)',
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: 16, color: '#EF4444' }} />
                          </IconButton>
                        </Box>
                        <Typography sx={{
                          fontSize: '0.75rem',
                          color: '#4B5563',
                          lineHeight: 1.2,
                          mt: 0.25,
                        }}>
                          {alert.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Time Selection */}
          <Box
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              flexShrink: 0,
              width: isFullscreen ? 192 : 160,
              p: 2,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.5 }}
            >
              <AccessTime sx={{ fontSize: 16, color: 'primary.main' }} />
              Hour
            </Typography>

            {availableTimeRanges.length > 0 ? (
              /* use CSS grid on a Box to replace deprecated Grid item/xs */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {availableTimeRanges.map((range, index) => (
                  <Box
                    key={index}
                    component="div"
                    sx={{
                      display: 'inline-block',
                      fontSize: '0.75rem',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      textAlign: 'center',
                      bgcolor: index === selectedRangeIndex ? '#D1FAE5' : '#ECFDF5',   // slightly darker
                      color: index === selectedRangeIndex ? '#065F46' : '#047857',     // darker text
                      borderColor: index === selectedRangeIndex ? '#34D399' : '#BBF7D0',
                    }}
                    onClick={() => showVideoForTimeRange(range, index)}
                  >
                    {range.start} - {range.end}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <Replay sx={{ fontSize: 32, color: 'grey.300', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  No recordings
                </Typography>
              </Box>
            )}
          </Box>

          {/* Video Player */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'scroll', height: '100%', pb: 2 }}>
            <Box sx={{ p: 2, pt: 1, display: 'flex', flexDirection: 'column', height: '80%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  Video Player
                </Typography>
                {selectedVideo?.videoUrl && (
                  <Button
                    onClick={() => setShowAddAlert(true)}
                    startIcon={<Add sx={{ fontSize: 12 }} />}
                    size="small"
                    sx={{
                      bgcolor: '#DBEAFE',
                      color: '#1D4ED8',
                      fontSize: '0.75rem',
                      '&:hover': { bgcolor: '#BFDBFE' },
                    }}
                  >
                    Add Alert
                  </Button>
                )}
              </Box>

              {/* Video Container */}
              <Box
                sx={{
                  bgcolor: 'grey.900',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  height: '100%',
                  mb: 2,
                }}
                className={selectedTime && isTimeInRange(selectedTime, availableTimeRanges) ? 'group' : ''}
              >
                {
                  <>
                    {/* Video Content */}
                    {selectedVideo?.videoUrl ? (
                      <video
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: 8,
                        }}
                        controls
                        ref={videoRef}
                        autoPlay={isPlaying}
                        muted={isMuted}
                        key={selectedVideo.videoUrl} // Force re-render when video changes
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onLoadedMetadata={(e) => {
                          const video = e.target;
                          video.playbackRate = playbackSpeed;
                          setVideoDuration(video.duration);
                        }}
                        onTimeUpdate={(e) => {
                          const time = e.target.currentTime;
                          setCurrentTime(time);
                          setCurrentProgress((time / e.target.duration) * 100 || 0);
                        }}
                      >
                        <source src={selectedVideo.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <Box sx={{ textAlign: 'center', color: 'white' }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: 'grey.600',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            mx: 'auto',
                          }}
                        >
                          <Box sx={{ width: 32, height: 32, bgcolor: 'grey.400', borderRadius: '50%' }} />
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          Select a time range to show the video
                        </Typography>
                      </Box>
                    )}
                  </>
                }
              </Box>
            </Box>

            {/* Fixed Controls at Bottom */}
            {selectedVideo?.videoUrl && (
              <Box
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  p: 2,
                  mb: 2,
                  bgcolor: 'grey.50',
                  height: '15%'
                }}
              >
                {/* Progress Bar with Timestamp and Alerts */}
                <Box sx={{ mb: 3, position: 'relative' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                    <Typography>{formatTime(currentTime)}</Typography>
                    <Typography fontWeight={500}>Recording Progress</Typography>
                    <Typography>{formatTime(videoDuration)}</Typography>
                  </Box>

                  {/* Timestamp Tooltip */}
                  {showTimestamp && hoverProgress !== null && (
                    <Tooltip
                      open
                      title={getTimestampFromProgress(hoverProgress)}
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'grey.800',
                            color: 'white',
                            fontSize: '0.75rem',
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            boxShadow: 3,
                            zIndex: 1300,
                            whiteSpace: 'nowrap',
                            transform: `translateX(-50%)`,
                            left: `${hoverProgress}%`,
                            position: 'absolute',
                            top: -48,
                          },
                        },
                      }}
                      PopperProps={{
                        sx: {
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: `${hoverProgress}%`,
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '4px solid grey.800',
                        }}
                      />
                    </Tooltip>
                  )}

                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'grey.300',
                      borderRadius: 9999,
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover .knob': { opacity: 1 },
                    }}
                    onClick={handleProgressClick}
                    onMouseMove={handleProgressMouseMove}
                    onMouseEnter={handleProgressMouseEnter}
                    onMouseLeave={handleProgressMouseLeave}
                  >
                    {/* Alert markers on progress bar */}
                    {currentHourAlerts.map((alert) => (
                      <Tooltip
                        key={alert.id}
                        title={`${alert.description} - ${alert.timestamp.split(' ')[1]} ${!alert.viewed ? '(NEW)' : ''}`}
                        placement="top"
                      >
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentProgress(alert.progress);
                            markAlertAsViewed(alert.id);
                          }}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: `${alert.progress}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 12,
                            height: 12,
                            bgcolor: getAlertTypeColor(alert.type),
                            border: 2,
                            borderColor: 'white',
                            boxShadow: 3,
                            '&:hover': { transform: 'translate(-50%, -50%) scale(1.1)' },
                            transition: 'transform 0.2s',
                            ...(!alert.viewed && { animation: 'pulse 2s infinite' }),
                          }}
                        />
                      </Tooltip>
                    ))}

                    <Box
                      sx={{
                        height: '100%',
                        bgcolor: 'primary.main',
                        borderRadius: 9999,
                        transition: 'all 0.3s',
                        position: 'relative',
                        width: `${currentProgress}%`,
                      }}
                    >
                      <Box
                        className="knob"
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translate(50%, -50%)',
                          width: 12,
                          height: 12,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          border: 1,
                          borderColor: 'white',
                          boxShadow: 1,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Control Panel */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Play/Pause Button */}
                    <Button
                      onClick={() => handlePlayPause()}
                      startIcon={isPlaying ? <Pause sx={{ fontSize: 14 }} /> : <PlayArrow sx={{ fontSize: 14 }} />}
                      size="small"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        transition: 'all 0.2s',
                        ...(isPlaying
                          ? { bgcolor: '#FEE2E2', color: '#B91C1C', '&:hover': { bgcolor: '#FECACA' } }
                          : { bgcolor: '#DBEAFE', color: '#1D4ED8', '&:hover': { bgcolor: '#BFDBFE' } }
                        ),
                      }}
                    >
                      {isPlaying ? 'Pause' : 'Play'}
                    </Button>

                    {/* Playback Speed Select */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Speed:
                      </Typography>
                      <Select
                        value={playbackSpeed}
                        onChange={(e) => handlePlaybackRateChange(e)}
                        size="small"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value={0.5}>0.5x</MenuItem>
                        <MenuItem value={1}>1x</MenuItem>
                        <MenuItem value={1.25}>1.25x</MenuItem>
                        <MenuItem value={1.5}>1.5x</MenuItem>
                        <MenuItem value={2}>2x</MenuItem>
                      </Select>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      onClick={() => setIsMuted(!isMuted)}
                      title={isMuted ? "Unmute" : "Mute"}
                      sx={{ '&:hover': { bgcolor: 'grey.200' } }}
                    >
                      {isMuted ? (
                        <VolumeOff sx={{ fontSize: 16, color: 'grey.600' }} />
                      ) : (
                        <VolumeUp sx={{ fontSize: 16, color: 'grey.600' }} />
                      )}
                    </IconButton>

                    {/* Current Frame Timestamp */}
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', bgcolor: 'background.paper', px: 2, py: 0.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ mb: 0.25 }}>
                        Current Frame
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {getTimestampFromProgress(currentProgress)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Add Alert Overlay - Rendered inside main modal to avoid nesting */}
          {showAddAlert && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
              onClick={() => {
                setShowAddAlert(false);
                setNewAlertDescription('');
              }}
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  p: 4,
                  width: { xs: '90vw', sm: 384 },
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflow: 'auto',
                }}
              >
                <Typography variant="h6" fontWeight={500} color="text.primary" sx={{ mb: 3 }}>
                  Add Alert
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ mb: 1 }}>
                      Timestamp
                    </Typography>
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', bgcolor: 'grey.50', px: 2, py: 1.5, borderRadius: 1 }}>
                      {getTimestampFromProgress(currentProgress)}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ mb: 1 }}>
                      Alert Type
                    </Typography>
                    <Select
                      fullWidth
                      value={newAlertType}
                      onChange={(e) => setNewAlertType(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ mb: 1 }}>
                      Description
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={newAlertDescription}
                      onChange={(e) => setNewAlertDescription(e.target.value)}
                      placeholder="Describe what happened at this moment..."
                      size="small"
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                  <Button
                    onClick={() => {
                      setShowAddAlert(false);
                      setNewAlertDescription('');
                    }}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addAlert}
                    disabled={!newAlertDescription.trim()}
                    variant="contained"
                    sx={{
                      opacity: !newAlertDescription.trim() ? 0.5 : 1,
                      cursor: !newAlertDescription.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Add Alert
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PlaybackModal;